<template>
  <VueMultiselect
      ref="vms"
      v-model="multiSelectValues"
      :multiple="multiple"
      :disabled="disabled"
      :clear-on-select="clearOnSelect"
      :options="options"
      :custom-label="customLabel"
      :placeholder="placeholder"
      :group-values="groupValues"
      :group-label="groupLabel"
      :group-select="groupSelect"
      :label="label"
      :track-by="trackBy"
      :close-on-select="closeOnSelect"
      :loading="loading"
      :select-label="selectLabel"
      :selected-label="selectedLabel"
      :deselect-label="deselectLabel"
      :hide-selected="hideSelected"
      :internal-search="internalSearch"
      @input="$emit('input', $event)"
      @select="$emit('select', $event)"
      @remove="$emit('remove', $event)"
      @search-change="$emit('search-change', $event)"
      @tag="$emit('tag', $event)"
      @open="$emit('open', $event)"
      @close="$emit('close', $event)">
    <template v-slot:noResult>
      {{ noResultsMessageText }}
    </template>
    <template
      v-for="(_, name) in $scopedSlots"
      v-slot:[name]="slotData">
      <slot
        :name="name"
        v-bind="slotData"
      />
    </template>
  </VueMultiselect>
</template>

<script>
import Multiselect from 'vue-multiselect';

export default {
  name: 'MultiSelect',
  components: {
     VueMultiselect: Multiselect,
  },
  props: {
    // 'value' is required for binding v-model to this component in the parent component
    value: {
      type: [String, Array, Number],
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    clearOnSelect: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: null,
    },
    customLabel: {
      type: Function,
    },
    closeOnSelect: {
      type: Boolean,
      default: true,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    groupValues: {
      type: String,
    },
    groupLabel: {
      type: String,
    },
    groupSelect: {
      type: Boolean,
    },
    label: {
      type: String,
    },
    trackBy: {
      type: String,
    },
    noResultsMessage: {
      type: String,
      required: false,
    },
    selectLabel: {
      type: String,
      required: false,
      default: '',
    },
    selectedLabel: {
      type: String,
      required: false,
      default: '',
    },
    deselectLabel: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideSelected: {
      type: Boolean,
      required: false,
      default: false,
    },
    internalSearch: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      multiSelectValues: this.value,
    };
  },
  computed: {
    noResultsMessageText() {
      return this.noResultsMessage ? this.noResultsMessage : this.$t('multiselect.noMatchesFound');
    },
  },
  watch: {
    value(newVal) {
      this.multiSelectValues = newVal;
      const elem = this.$refs.vms.$el.querySelector('.multiselect__tags input');

      /**
       * Adjust width of input depending on placeholder so that it stays on the same line
       * as the selected items for as long as possible before moving to the next line
       */
      // if an item is selected, the placeholder text is short
      if (newVal && newVal.length > 0) {
        // set input width to narrow
        elem.classList.add('max-w-160');
        elem.classList.remove('max-w-350');

        // else placeholder text is long
      } else {
        // set input width to wide
        elem.classList.add('max-w-350');
        elem.classList.remove('max-w-160');
      }
    },
    hasError(newVal) {
      const elem = this.$refs.vms.$el.querySelector('.multiselect__tags');
      elem.classList.toggle('tw-border-error', newVal);
    },
  },
};
</script>
<!-- Add Multiselect CSS. Can be added as a static asset or inside a component. -->
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
.max-w-160 {
  max-width: 160px;
}

.max-w-350 {
  max-width: 350px;
}

.multiselect, .multiselect__input, .multiselect__single {
  font-size: var(--font-size-small);
  color: #606266;
}

.multiselect__tag {
  background-color: var(--signable-secondary-3-shade-3);
  color: var(--color-placeholder-text);
  border-radius: 30px;
  padding-top: 10px;
  padding-bottom: 10px;
}

.multiselect__tag-icon {
  line-height: 33px;
}

.multiselect__tag-icon:hover, .multiselect__tag-icon:focus {
  background-color: var(--signable-secondary-2-shade-2);
}

.multiselect__tag-icon:focus-visible {
  outline: none;
}

.multiselect__placeholder {
  font-weight: 400;
  font-size: 16px;
  margin-top: 6px;
  margin-left: 5px;
}

.multiselect__option--highlight {
  color: #606266;
  background-color: #F5F7FA;
}

.multiselect__option--highlight::after {
  color: #606266;
  background-color: #F5F7FA;
}

.multiselect__option::after {
  display: flex;
  height: 100%;
  align-items: center;
}

.multiselect__tags {
  min-height: 52px;
}

.multiselect__tags input {
  position: relative;
  width: auto;
  margin-top: 7px;
}

.multiselect__select {
  top: 7px;
}

.multiselect--disabled .multiselect__select {
  background: transparent;
}

.multiselect__spinner {
  top: 7px;
}

.multiselect__spinner::before {
  border-top-color: var(--signable-primary-1);
}

.multiselect__spinner::after {
  border-top-color: var(--signable-primary-1);
}
</style>
